import type { PatchOperation } from "@medplum/core";
import type { Resource } from "@medplum/fhirtypes";
import { isMedplumVersionConflictError } from "@/lib/errors/is-medplum-version-conflict-error";

const DEFAULT_MAX_PATCH_ATTEMPTS = 3;

export interface PatchWithVersionRetryParams<T extends Resource> {
  /** `Type/id`, for error messages. */
  label: string;
  buildOperations: (args: { current: T }) => PatchOperation[] | undefined;
  initial: T;
  loadCurrent: () => Promise<T>;
  maxAttempts?: number;
  missingVersionErrorMessage?: string;
  patch: (args: { operations: PatchOperation[] }) => Promise<T>;
}

/**
 * Applies a JSON patch guarded by `meta.versionId`, retrying on optimistic conflicts. Each attempt
 * rebuilds its operations from the version it patches. No operations means no write: the stored
 * version comes back unchanged.
 */
export async function patchWithVersionRetry<T extends Resource>({
  label,
  buildOperations,
  initial,
  loadCurrent,
  maxAttempts = DEFAULT_MAX_PATCH_ATTEMPTS,
  missingVersionErrorMessage,
  patch,
}: PatchWithVersionRetryParams<T>): Promise<T> {
  let current = initial;
  let lastError: unknown;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const versionId = current.meta?.versionId;
    if (!versionId) {
      throw new Error(
        missingVersionErrorMessage ?? `Cannot update ${label}: missing meta.versionId`,
      );
    }

    const operations = buildOperations({ current });
    if (!operations || operations.length === 0) {
      return current;
    }

    try {
      return await patch({
        operations: [{ op: "test", path: "/meta/versionId", value: versionId }, ...operations],
      });
    } catch (error) {
      if (!isMedplumVersionConflictError(error) || attempt === maxAttempts - 1) {
        throw error;
      }

      lastError = error;
      current = await loadCurrent();
    }
  }

  throw lastError ?? new Error(`Failed to patch ${label}`);
}
